import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase/supabaseClient';

interface CafeOwner {
  id: number;
  created_at: string;
  owner_name: string;
  cafe_name: string;
  address: string | null;
  city: string;
  state: string | null;
  country: string | null;
  google_maps_link: string | null;
  total_tables: number | null;
  open_hours: string | null;
  interested_in_bell: boolean;
  wifi_available: boolean;
  phone_number: string | null;
  email: string | null;
  preferred_contact: string | null;
  best_time: string | null;
  notes: string | null;
}

type SortField = 'created_at' | 'owner_name' | 'cafe_name' | 'city';
type SortDir = 'asc' | 'desc';

function Badge({ value, trueLabel = 'Yes', falseLabel = 'No' }: { value: boolean; trueLabel?: string; falseLabel?: string }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${value ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-neutral-100 text-neutral-500 border border-neutral-200'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${value ? 'bg-emerald-500' : 'bg-neutral-400'}`} />
      {value ? trueLabel : falseLabel}
    </span>
  );
}

function ContactBadge({ method }: { method: string | null }) {
  const map: Record<string, { label: string; color: string }> = {
    phone: { label: 'Phone', color: 'bg-blue-50 text-blue-700 border-blue-200' },
    email: { label: 'Email', color: 'bg-violet-50 text-violet-700 border-violet-200' },
    either: { label: 'Any', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  };
  const item = map[method ?? 'either'] ?? map['either'];
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${item.color}`}>
      {item.label}
    </span>
  );
}

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-2xl px-5 py-4">
      <p className="text-xs text-neutral-400 font-medium uppercase tracking-wider mb-1">{label}</p>
      <p className="text-2xl font-bold text-neutral-900">{value}</p>
      {sub && <p className="text-xs text-neutral-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function AdminPage() {
  const [entries, setEntries] = useState<CafeOwner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selected, setSelected] = useState<CafeOwner | null>(null);
  const [filterBell, setFilterBell] = useState<'all' | 'yes' | 'no'>('all');

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error } = await supabase
        .from('cafe_owners')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setEntries(data || []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const filtered = entries
    .filter(e => {
      const q = search.toLowerCase();
      const matchSearch = !q ||
        e.owner_name.toLowerCase().includes(q) ||
        e.cafe_name.toLowerCase().includes(q) ||
        e.city.toLowerCase().includes(q) ||
        (e.phone_number ?? '').includes(q) ||
        (e.email ?? '').toLowerCase().includes(q);
      const matchBell = filterBell === 'all' || (filterBell === 'yes' ? e.interested_in_bell : !e.interested_in_bell);
      return matchSearch && matchBell;
    })
    .sort((a, b) => {
      const av = a[sortField] ?? '';
      const bv = b[sortField] ?? '';
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });

  const bellCount = entries.filter(e => e.interested_in_bell).length;
  const wifiCount = entries.filter(e => e.wifi_available).length;
  const avgTables = entries.length
    ? Math.round(entries.filter(e => e.total_tables).reduce((s, e) => s + (e.total_tables ?? 0), 0) / (entries.filter(e => e.total_tables).length || 1))
    : 0;

  const SortIcon = ({ field }: { field: SortField }) => (
    <span className={`ml-1 inline-block transition-opacity ${sortField === field ? 'opacity-100' : 'opacity-30'}`}>
      {sortField === field && sortDir === 'desc' ? '↓' : '↑'}
    </span>
  );

  const fmt = (iso: string) => new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div className="min-h-screen bg-neutral-50 font-sans">
      {/* Top bar */}
      <header className="bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-neutral-900 flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
          <div>
            <h1 className="text-sm font-bold text-neutral-900 leading-none">Bell Admin</h1>
            <p className="text-xs text-neutral-400 mt-0.5">Cafe registrations</p>
          </div>
        </div>
        <button
          onClick={fetchEntries}
          className="flex items-center gap-2 text-xs font-medium text-neutral-600 hover:text-neutral-900 border border-neutral-200 hover:border-neutral-400 px-3 py-1.5 rounded-lg transition-all"
        >
          <svg className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Total Registrations" value={entries.length} sub="all time" />
          <StatCard label="Bell Service Interest" value={bellCount} sub={`${entries.length ? Math.round(bellCount / entries.length * 100) : 0}% of signups`} />
          <StatCard label="WiFi Available" value={wifiCount} sub="cafes with WiFi" />
          <StatCard label="Avg. Tables" value={avgTables || '—'} sub="per cafe" />
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search by name, cafe, city, phone, email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent bg-white placeholder:text-neutral-400"
            />
          </div>
          <div className="flex gap-2">
            {(['all', 'yes', 'no'] as const).map(v => (
              <button
                key={v}
                onClick={() => setFilterBell(v)}
                className={`px-4 py-2.5 text-xs font-medium rounded-xl border transition-all ${filterBell === v ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400'}`}
              >
                {v === 'all' ? 'All' : v === 'yes' ? '🔔 Bell: Yes' : 'Bell: No'}
              </button>
            ))}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{error}</div>
        )}

        {/* Table */}
        <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-neutral-400 text-sm gap-2">
              <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Loading entries…
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-neutral-400 gap-2">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm">No entries found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-100 bg-neutral-50">
                    {[
                      { label: 'Date', field: 'created_at' as SortField },
                      { label: 'Owner', field: 'owner_name' as SortField },
                      { label: 'Cafe', field: 'cafe_name' as SortField },
                      { label: 'City', field: 'city' as SortField },
                    ].map(col => (
                      <th
                        key={col.field}
                        onClick={() => handleSort(col.field)}
                        className="text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wider cursor-pointer hover:text-neutral-900 transition-colors select-none whitespace-nowrap"
                      >
                        {col.label}<SortIcon field={col.field} />
                      </th>
                    ))}
                    <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wider whitespace-nowrap">Tables</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wider whitespace-nowrap">Bell</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wider whitespace-nowrap">Contact</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wider whitespace-nowrap">Reach via</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {filtered.map(entry => (
                    <tr key={entry.id} className="hover:bg-neutral-50 transition-colors group">
                      <td className="px-4 py-3 text-neutral-400 text-xs whitespace-nowrap">{fmt(entry.created_at)}</td>
                      <td className="px-4 py-3 font-medium text-neutral-900 whitespace-nowrap">{entry.owner_name}</td>
                      <td className="px-4 py-3 text-neutral-700 whitespace-nowrap">{entry.cafe_name}</td>
                      <td className="px-4 py-3 text-neutral-500 whitespace-nowrap">{entry.city}{entry.state ? `, ${entry.state}` : ''}</td>
                      <td className="px-4 py-3 text-neutral-500 text-center">{entry.total_tables ?? '—'}</td>
                      <td className="px-4 py-3"><Badge value={entry.interested_in_bell} trueLabel="Interested" falseLabel="No" /></td>
                      <td className="px-4 py-3 text-neutral-500 text-xs whitespace-nowrap">
                        {entry.phone_number || entry.email ? (
                          <span>{entry.phone_number ?? entry.email}</span>
                        ) : <span className="text-neutral-300">—</span>}
                      </td>
                      <td className="px-4 py-3"><ContactBadge method={entry.preferred_contact} /></td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelected(entry)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-xs font-medium text-neutral-900 border border-neutral-200 hover:border-neutral-900 px-3 py-1 rounded-lg"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-3 border-t border-neutral-100 text-xs text-neutral-400">
                Showing {filtered.length} of {entries.length} entries
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Detail Drawer */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex justify-end" onClick={() => setSelected(null)}>
          <div
            className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Drawer header */}
            <div className="sticky top-0 bg-white border-b border-neutral-100 px-6 py-5 flex items-start justify-between z-10">
              <div>
                <h2 className="font-bold text-neutral-900 text-base">{selected.cafe_name}</h2>
                <p className="text-xs text-neutral-400 mt-0.5">{selected.owner_name} · #{selected.id}</p>
              </div>
              <button onClick={() => setSelected(null)} className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-400 hover:text-neutral-900 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="px-6 py-5 space-y-6">

              {/* Location */}
              <Section title="Location">
                <Row label="Address" value={[selected.address, selected.city, selected.state, selected.country].filter(Boolean).join(', ')} />
                {selected.google_maps_link && (
                  <Row label="Maps">
                    <a href={selected.google_maps_link} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-sm truncate block">
                      Open in Google Maps ↗
                    </a>
                  </Row>
                )}
              </Section>

              {/* Cafe Setup */}
              <Section title="Cafe Setup">
                <Row label="Tables" value={selected.total_tables != null ? String(selected.total_tables) : '—'} />
                <Row label="Opening Hours" value={selected.open_hours ?? '—'} />
              </Section>

              {/* Bell Service */}
              <Section title="Digital Bell Service">
                <Row label="Interested"><Badge value={selected.interested_in_bell} trueLabel="Yes" falseLabel="No" /></Row>
                <Row label="WiFi Available"><Badge value={selected.wifi_available} trueLabel="Yes" falseLabel="No" /></Row>
              </Section>

              {/* Contact */}
              <Section title="Contact">
                <Row label="Phone" value={selected.phone_number ?? '—'} />
                <Row label="Email" value={selected.email ?? '—'} />
                <Row label="Preferred"><ContactBadge method={selected.preferred_contact} /></Row>
                <Row label="Best Time" value={selected.best_time ?? '—'} />
              </Section>

              {/* Notes */}
              {selected.notes && (
                <Section title="Notes">
                  <p className="text-sm text-neutral-600 leading-relaxed bg-neutral-50 rounded-xl p-3 border border-neutral-100">{selected.notes}</p>
                </Section>
              )}

              <p className="text-xs text-neutral-300 pb-4">Submitted {new Date(selected.created_at).toLocaleString('en-IN')}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-widest text-neutral-400 mb-3">{title}</p>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-xs text-neutral-400 shrink-0 pt-0.5 w-24">{label}</span>
      <span className="text-sm text-neutral-800 text-right">{children ?? value}</span>
    </div>
  );
}